import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  gasAdminRegister,
  validateInvite,
  type ErrorResponse,
  type InviteValidationResponse,
} from '@vn-propane/shared';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { LanguageSelector } from '@/components/ui/language-selector';
import { LegalFooter } from '@vn-propane/shared/components';

const MARKETING_BASE_URL = 'https://giaogas.com';

export default function RegisterPage() {
  const { t, i18n } = useTranslation(['auth', 'common', 'errors', 'admin']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('inviteToken');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [invite, setInvite] = useState<InviteValidationResponse | null>(null);
  const [inviteInvalid, setInviteInvalid] = useState(false);

  const legalUrl = (page: string) => {
    const langPrefix = i18n.language === 'en' ? '/en' : '';
    return `${MARKETING_BASE_URL}${langPrefix}/${page}/`;
  };

  // If the URL carries an invite token, validate it so we can render the
  // banner, prefill+lock the email, and bounce existing users to login.
  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    (async () => {
      const result = await validateInvite({ query: { token: inviteToken } });
      if (cancelled) return;
      if (result.data) {
        const data = result.data.data;
        setInvite(data);
        setEmail(data.email);
        if (data.alreadyRegistered) {
          navigate(`/login?inviteToken=${encodeURIComponent(inviteToken)}`, { replace: true });
        }
      } else {
        setInviteInvalid(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteToken, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!agreedToTerms) {
      setError(t('auth:legal.mustAgree'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('validation.passwordsDoNotMatch'));
      return;
    }

    setIsLoading(true);

    try {
      const result = await gasAdminRegister({
        body: {
          username,
          email,
          password,
          ...(inviteToken ? { inviteToken } : {}),
        },
      });

      if (result.data) {
        navigate('/login');
        return;
      }

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        setError(errorResponse.error?.message || t('validation.registrationFailed'));
      } else {
        setError(t('validation.registrationFailed'));
      }
    } catch (err) {
      setError(t('errors:unexpectedError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-muted/40 relative">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      <div className="w-full max-w-md mx-4 p-6 md:p-8 bg-background rounded-sm border shadow-sm">
        <div className="flex flex-col items-center mb-6">
          <img src={`${import.meta.env.BASE_URL}logo-mark-dark.png`} alt="Giao Gas" className="h-12 w-12 mb-3" />
          <h1 className="text-2xl font-extrabold tracking-tight text-center">
            giao<span className="text-propane-500">gas</span>
            <span className="block text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mt-1">
              {t('admin.registration')}
            </span>
          </h1>
          <p className="text-muted-foreground text-sm text-center mt-2">
            {t('admin.registrationDescription')}
          </p>
        </div>
        {invite && (
          <Alert className="mb-4">
            <AlertDescription>
              {t('admin:members.invitedBanner', {
                organization: invite.organizationName,
                role:
                  invite.role === 'admin'
                    ? t('admin:members.roleAdmin')
                    : t('admin:members.roleMember'),
              })}
            </AlertDescription>
          </Alert>
        )}
        {inviteInvalid && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{t('admin:members.inviteInvalid')}</AlertDescription>
          </Alert>
        )}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="username">{t('fields.username')}</Label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">{t('fields.email')}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading || invite !== null}
              readOnly={invite !== null}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t('fields.password')}</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">{t('fields.confirmPassword')}</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>
          <div className="flex items-start gap-2">
            <Checkbox
              id="terms"
              checked={agreedToTerms}
              onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
              disabled={isLoading}
              className="mt-0.5"
            />
            <Label htmlFor="terms" className="text-sm font-normal leading-snug cursor-pointer">
              {t('auth:legal.agreementPrefix')}{' '}
              <a href={legalUrl('terms')} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:no-underline">
                {t('auth:legal.termsAndConditions')}
              </a>
              {' '}{t('auth:legal.agreementAnd')}{' '}
              <a href={legalUrl('privacy')} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:no-underline">
                {t('auth:legal.privacyPolicy')}
              </a>
            </Label>
          </div>
          <Button type="submit" disabled={isLoading || !agreedToTerms} className="w-full">
            {isLoading ? t('registering') : t('register')}
          </Button>
        </form>
        <p className="text-sm text-center mt-4 text-muted-foreground">
          {t('links.alreadyHaveAccount')}{' '}
          <Link
            to={inviteToken ? `/login?inviteToken=${encodeURIComponent(inviteToken)}` : '/login'}
            className="text-primary hover:underline"
          >
            {t('signIn')}
          </Link>
        </p>
      </div>
      <LegalFooter className="absolute bottom-6" />
    </div>
  );
}
